import { useEffect, useMemo, useState } from 'react';
import { contractsApi, type Contract, type ContractKind, type ContractStatus, type CreateContractInput } from '../lib/api/contracts';
import { employeesApi } from '../lib/api/employees';
import { SearchableDropdown } from './SearchableDropdown';
import { attachmentsApi, type UploadedAttachment } from '../lib/api/attachments';

interface Props {
  open: boolean;
  editingId?: string | null;
  defaultKind?: ContractKind;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
  /** AI import upload trước, sau đó mở form với tài liệu đã gắn chờ lưu. */
  initialAttachment?: UploadedAttachment | null;
  initialName?: string;
}

const STATUS_OPTIONS: Array<{ code: ContractStatus; label: string }> = [
  { code: 'active', label: 'Đang thực hiện' },
  { code: 'paused', label: 'Tạm dừng' },
  { code: 'completed', label: 'Hoàn tất' },
  { code: 'cancelled', label: 'Đã huỷ' },
];

const SERVICE_GROUPS_REVENUE = [
  { label: 'Software', options: ['Bảo trì hệ thống', 'Phát triển phần mềm', 'Tư vấn', 'License'] },
  { label: 'Drone Show', options: ['Show sự kiện'] },
  { label: 'Khác', options: ['Marketing', 'Consulting', 'Khác'] },
];

const SERVICE_GROUPS_EXPENSE = [
  { label: 'Vận hành', options: ['Thuê văn phòng', 'Điện nước', 'Internet · Cloud', 'Bảo hiểm', 'Bảo trì tài sản'] },
  { label: 'Mua sắm', options: ['Trang thiết bị', 'Vật tư · Văn phòng phẩm', 'Phần mềm · License'] },
  { label: 'Dịch vụ thuê ngoài', options: ['Marketing outsource', 'Tư vấn pháp lý', 'Kiểm toán', 'Vệ sinh · An ninh', 'Vận chuyển'] },
  { label: 'Khác', options: ['Khác'] },
];

const SALESPERSONS_REVENUE = ['Nguyễn Văn A · Trưởng phòng KD', 'Trần Thị B · Marketing Lead', 'Lê Văn C · Kỹ thuật viên', 'Phạm Minh Đức · CEO'];
const PURCHASERS = ['Nguyễn Thị Mai · Trưởng phòng HCNS', 'Trần Văn Bình · Trưởng phòng KT', 'Phạm Minh Đức · CEO', 'Lê Thị Hạnh · Kế toán trưởng'];

interface ScheduleRow {
  id: string;
  dueDate: string;
  amount: number;
  note: string;
  existingId?: string;
}

function fmtVND(n: number): string {
  return n.toLocaleString('vi-VN');
}

function toIsoDate(v: string | null | undefined): string {
  if (!v) return '';
  return v.slice(0, 10);
}

function suggestCode(kind: ContractKind): string {
  const d = new Date();
  const rand = String(Math.floor(Math.random() * 9999)).padStart(4, '0');
  const prefix = kind === 'expense' ? 'HDC' : 'HD';
  return `${prefix}-${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${rand}`;
}

function addMonths(iso: string, n: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
}

export function ContractFormModal({ open, editingId, defaultKind = 'revenue', onClose, onSaved, onDeleted, initialAttachment, initialName }: Props) {
  const isEdit = Boolean(editingId);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [kind, setKind] = useState<ContractKind>(defaultKind);
  const isExpense = kind === 'expense';
  const serviceGroups = isExpense ? SERVICE_GROUPS_EXPENSE : SERVICE_GROUPS_REVENUE;
  const [hrContacts, setHrContacts] = useState<string[]>([]);
  const contactList = hrContacts.length ? hrContacts : (isExpense ? PURCHASERS : SALESPERSONS_REVENUE);
  const partyLabel = isExpense ? 'Nhà cung cấp' : 'Khách hàng';
  const contactLabel = isExpense ? 'Người phụ trách mua' : 'Người phụ trách';

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  const [customerTaxCode, setCustomerTaxCode] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerContact, setCustomerContact] = useState('');
  const [salesperson, setSalesperson] = useState(contactList[0]);
  const [valueInput, setValueInput] = useState('');
  const [serviceType, setServiceType] = useState(serviceGroups[0].options[0]);
  const [businessUnit, setBusinessUnit] = useState(serviceGroups[0].label);
  const [latePenaltyRate, setLatePenaltyRate] = useState<number | ''>(1.5);
  const [signedAt, setSignedAt] = useState(new Date().toISOString().slice(0, 10));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<ContractStatus>('active');
  const [notes, setNotes] = useState('');
  const [schedule, setSchedule] = useState<ScheduleRow[]>([]);

  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    if (!open) return;
    employeesApi.picker()
      .then((items) => {
        const contacts = items.filter((item) => item.active !== false).map((item) => `${item.name}${item.position ? ` · ${item.position}` : ''}`);
        setHrContacts(contacts);
        setSalesperson((current) => contacts.includes(current) ? current : (contacts[0] ?? current));
      })
      .catch(() => setHrContacts([]));
  }, [open]);

  const value = Number(valueInput.replace(/\D/g, '')) || 0;
  const scheduleTotal = useMemo(() => schedule.reduce((s, r) => s + r.amount, 0), [schedule]);
  const scheduleMatch = value > 0 && scheduleTotal === value;
  const scheduleDiff = value - scheduleTotal;

  const handleValueChange = (raw: string) => {
    setValueInput(raw);
    if (isEdit) return;
    const v = Number(raw.replace(/\D/g, '')) || 0;
    if (v < 1_000_000) return;
    // Chỉ populate lần đầu (khi schedule còn rỗng) — nếu user đã có schedule, không đè
    if (schedule.length > 0) {
      // Re-split đều 6 kỳ theo value mới nếu tất cả rows là auto-generated
      const allAuto = schedule.every((r) => r.id.startsWith('auto-'));
      if (!allAuto) return;
    }
    const n = 6;
    const each = Math.floor(v / n);
    const remainder = v - each * n;
    const base = signedAt || new Date().toISOString().slice(0, 10);
    setSchedule(
      Array.from({ length: n }, (_, i) => ({
        id: `auto-${Date.now()}-${i}`,
        dueDate: addMonths(base, i),
        amount: i === n - 1 ? each + remainder : each,
        note: i === 0 ? 'Trả trước khi ký' : '',
      })),
    );
  };

  useEffect(() => {
    if (!open) return;
    setErr(null); setSaving(false); setDeleting(false);
    setKind(defaultKind);
    const initGroups = defaultKind === 'expense' ? SERVICE_GROUPS_EXPENSE : SERVICE_GROUPS_REVENUE;
    const initContacts = defaultKind === 'expense' ? PURCHASERS : SALESPERSONS_REVENUE;
    setCode(suggestCode(defaultKind)); setName(initialName ?? ''); setCustomer('');
    setCustomerTaxCode(''); setCustomerAddress(''); setCustomerContact('');
    setSalesperson(initContacts[0]);
    setValueInput('');
    setServiceType(initGroups[0].options[0]); setBusinessUnit(initGroups[0].label);
    setLatePenaltyRate(1.5);
    setSignedAt(new Date().toISOString().slice(0, 10));
    setStartDate(''); setEndDate('');
    setStatus('active');
    setNotes('');
    // Khởi tạo 6 kỳ mặc định theo tháng (amount = 0) để user luôn thấy khung table
    const base = new Date().toISOString().slice(0, 10);
    setSchedule(
      Array.from({ length: 6 }, (_, i) => ({
        id: `auto-${Date.now()}-${i}`,
        dueDate: addMonths(base, i),
        amount: 0,
        note: i === 0 ? 'Trả trước khi ký' : '',
      })),
    );
    setFiles([]);
    setUploadedFiles(initialAttachment ? [initialAttachment] : []);

    if (editingId) {
      setLoadingEdit(true);
      contractsApi.findOne(editingId)
        .then(fillFromContract)
        .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoadingEdit(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId, initialAttachment, initialName]);

  const fillFromContract = (c: Contract) => {
    setKind(c.kind);
    const groups = c.kind === 'expense' ? SERVICE_GROUPS_EXPENSE : SERVICE_GROUPS_REVENUE;
    const contacts = c.kind === 'expense' ? PURCHASERS : SALESPERSONS_REVENUE;
    setCode(c.code);
    setName(c.name);
    setCustomer(c.customer);
    setCustomerTaxCode(c.customerTaxCode ?? '');
    setCustomerAddress(c.customerAddress ?? '');
    setCustomerContact(c.customerContact ?? '');
    setSalesperson(c.salesperson ?? contacts[0]);
    setValueInput(String(c.value));
    setServiceType(c.serviceType ?? groups[0].options[0]);
    setBusinessUnit(c.businessUnit ?? groups[0].label);
    setLatePenaltyRate(c.latePenaltyRate ?? 0);
    setSignedAt(toIsoDate(c.signedAt));
    setStartDate(toIsoDate(c.startDate));
    setEndDate(toIsoDate(c.endDate));
    setStatus(c.status);
    setNotes(c.notes ?? '');
    setSchedule(
      (c.payments ?? []).map((p) => ({
        id: `p${p.id}`,
        existingId: p.id,
        dueDate: toIsoDate(p.dueDate),
        amount: p.amount,
        note: '',
      })),
    );
  };

  if (!open) return null;

  // Schedule generators
  const splitEqualMonthly = () => {
    if (!value || value < 1000) return setErr('Nhập giá trị HĐ trước khi chia lịch.');
    const n = 6;
    const each = Math.floor(value / n);
    const remainder = value - each * n;
    const rows: ScheduleRow[] = Array.from({ length: n }, (_, i) => ({
      id: `new-${Date.now()}-${i}`,
      dueDate: addMonths(signedAt || new Date().toISOString().slice(0, 10), i),
      amount: i === n - 1 ? each + remainder : each,
      note: i === 0 ? 'Trả trước khi ký' : '',
    }));
    setSchedule(rows);
    setErr(null);
  };
  const splitEqualQuarterly = () => {
    if (!value || value < 1000) return setErr('Nhập giá trị HĐ trước khi chia lịch.');
    const n = 4;
    const each = Math.floor(value / n);
    const remainder = value - each * n;
    const rows: ScheduleRow[] = Array.from({ length: n }, (_, i) => ({
      id: `new-${Date.now()}-${i}`,
      dueDate: addMonths(signedAt || new Date().toISOString().slice(0, 10), i * 3),
      amount: i === n - 1 ? each + remainder : each,
      note: '',
    }));
    setSchedule(rows);
    setErr(null);
  };
  const addRow = () => {
    setSchedule((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}-${prev.length}`,
        dueDate: prev.length > 0 ? addMonths(prev[prev.length - 1].dueDate, 1) : signedAt,
        amount: 0,
        note: '',
      },
    ]);
  };
  const updateRow = (id: string, patch: Partial<ScheduleRow>) => {
    setSchedule((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id: string) => {
    setSchedule((prev) => prev.filter((r) => r.id !== id));
  };

  const submitCore = async (asDraft: boolean) => {
    setErr(null);
    if (!isEdit && code.trim().length < 4) return setErr('Mã HĐ tối thiểu 4 ký tự.');
    if (customer.trim().length < 2) return setErr(`Tên ${isExpense ? 'nhà cung cấp' : 'khách hàng'} tối thiểu 2 ký tự.`);
    if (name.trim().length < 2) return setErr('Tên HĐ tối thiểu 2 ký tự.');
    if (value < 1_000_000) return setErr('Giá trị HĐ tối thiểu 1 triệu.');
    if (!asDraft) {
      if (schedule.length === 0) return setErr('Cần ít nhất 1 kỳ thanh toán.');
      if (!scheduleMatch) {
        return setErr(
          `Tổng lịch thanh toán (${fmtVND(scheduleTotal)}đ) không khớp giá trị HĐ (${fmtVND(value)}đ). ` +
          `${scheduleDiff > 0 ? 'Thiếu' : 'Thừa'} ${fmtVND(Math.abs(scheduleDiff))}đ.`,
        );
      }
    }

    const payload: CreateContractInput = {
      code: code.trim(),
      kind,
      customer: customer.trim(),
      name: name.trim(),
      value,
      signedAt,
      periodsTotal: Math.max(1, schedule.length),
      periodsPaid: 0,
      status: asDraft ? 'paused' : status,
      customerTaxCode: customerTaxCode.trim() || undefined,
      customerAddress: customerAddress.trim() || undefined,
      customerContact: customerContact.trim() || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      businessUnit: businessUnit.trim() || undefined,
      serviceType: serviceType.trim() || undefined,
      salesperson: salesperson.trim() || undefined,
      latePenaltyRate: latePenaltyRate === '' ? undefined : Number(latePenaltyRate),
      notes: notes.trim() || undefined,
      attachmentIds: uploadedFiles.map((file) => file.id),
    };

    if (asDraft) setSavingDraft(true); else setSaving(true);
    try {
      let contractId = editingId;
      if (files.length) {
        setUploadingFiles(true);
        const uploaded = await Promise.all(files.map((file) => attachmentsApi.upload(file)));
        payload.attachmentIds = [...(payload.attachmentIds ?? []), ...uploaded.map((file) => file.id)];
      }
      if (isEdit && editingId) {
        const { code: _c, kind: _k, ...rest } = payload;
        await contractsApi.update(editingId, rest);
      } else {
        const created = await contractsApi.create(payload);
        contractId = created.id;
      }
      if (contractId) {
        const existingRows = schedule.filter((r) => r.existingId);
        const newRows = schedule.filter((r) => !r.existingId);
        for (let i = 0; i < existingRows.length; i++) {
          const r = existingRows[i];
          await contractsApi.updatePayment(r.existingId!, { dueDate: r.dueDate, amount: r.amount });
        }
        for (let i = 0; i < newRows.length; i++) {
          const r = newRows[i];
          await contractsApi.addPayment(contractId, {
            periodIndex: existingRows.length + i + 1,
            dueDate: r.dueDate,
            amount: r.amount,
          });
        }
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setUploadingFiles(false);
      if (asDraft) setSavingDraft(false); else setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitCore(false);
  };
  const handleSaveDraft = () => submitCore(true);

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm(`Xoá HĐ ${code} - ${name}?`)) return;
    setDeleting(true); setErr(null);
    try {
      await contractsApi.remove(editingId);
      onDeleted?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setDeleting(false); }
  };

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="ctf2-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          {/* Header */}
          <div className="ctf2-head">
            <div className="ctf2-head-left">
              <div className="ctf2-head-icon">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 12h1M9 16h6M9 8h1" /></svg>
              </div>
              <div>
                <h3>{isEdit ? `Sửa hợp đồng ${code}` : (isExpense ? 'Thêm HĐ chi (NCC)' : 'Thêm HĐ thu (KH)')}</h3>
                <p>Điền thông tin hợp đồng và lịch {isExpense ? 'chi trả' : 'thanh toán'}</p>
              </div>
            </div>
            <button type="button" className="ctf2-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Body */}
          <div className="ctf2-body">
            {loadingEdit && <div className="pay-empty">Đang tải dữ liệu…</div>}

            {/* Section 1: Thông tin cơ bản */}
            <div className="ctf2-card">
              <div className="ctf2-card-title">Thông tin cơ bản</div>

              <div className="ctf2-grid" style={{ gridTemplateColumns: '1fr 2fr' }}>
                <Field label="Mã hợp đồng" required>
                  <input value={code} onChange={(e) => setCode(e.target.value)} disabled={isEdit} required style={{ fontWeight: 600 }} />
                  <div className="ctf2-hint">Auto-gen theo format {isExpense ? 'HDC' : 'HD'}-YYYY/MM/NNNN</div>
                </Field>
                <Field label="Tên hợp đồng" required>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={isExpense ? 'VD Thuê văn phòng T7-T12/2026' : 'VD Duy trì ERP T7-T12/2026'} required />
                </Field>
              </div>

              <div className="ctf2-grid ctf2-grid-3" style={{ marginTop: 12 }}>
                <Field label={partyLabel} required>
                  <div className="ctf2-input-with-icon">
                    <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder={isExpense ? 'Cty CP Bất động sản ABC — MST: 0301...' : 'XYZ Trading — MST: 0301112233'} required />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                  </div>
                </Field>
                <Field label={contactLabel}>
                  <SearchableDropdown value={salesperson} onChange={setSalesperson} options={contactList.map((item) => ({ value: item, label: item }))} ariaLabel={contactLabel} />
                </Field>
                <Field label="Giá trị hợp đồng" required>
                  <div className="ctf2-input-with-suffix">
                    <input
                      inputMode="numeric" placeholder="540.000.000"
                      value={value ? fmtVND(value) : ''}
                      onChange={(e) => handleValueChange(e.target.value)}
                      required
                      style={{ fontSize: 16, fontWeight: 700, textAlign: 'right', paddingRight: 28 }}
                    />
                    <span>đ</span>
                  </div>
                </Field>
              </div>

              <div className="ctf2-grid ctf2-grid-3" style={{ marginTop: 12 }}>
                <Field label="Loại hợp đồng">
                  <SearchableDropdown value={serviceType} onChange={(nextValue) => {
                    setServiceType(nextValue);
                    const grp = serviceGroups.find((g) => g.options.includes(nextValue));
                    if (grp) setBusinessUnit(grp.label);
                  }} options={serviceGroups.flatMap((group) => group.options.map((item) => ({ value: item, label: `${group.label} · ${item}` })))} ariaLabel="Loại hợp đồng" />
                </Field>
                <Field label="Lãi suất phạt trễ (%/tháng)">
                  <div className="ctf2-input-with-suffix">
                    <input
                      type="number" min={0} step="0.1"
                      value={latePenaltyRate}
                      onChange={(e) => setLatePenaltyRate(e.target.value === '' ? '' : Number(e.target.value))}
                      style={{ textAlign: 'right', fontWeight: 600 }}
                    />
                    <span>%/th</span>
                  </div>
                  <div className="ctf2-hint">VD 1.5%/tháng ≈ 18%/năm. Set 0 nếu không tính lãi.</div>
                </Field>
                <Field label="Ngày ký" required>
                  <input type="date" value={signedAt} onChange={(e) => setSignedAt(e.target.value)} required />
                </Field>
              </div>

              <div className="ctf2-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                <Field label="Ngày hết hạn">
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </Field>
                <Field label="Trạng thái">
                  <SearchableDropdown value={status} onChange={(nextValue) => setStatus(nextValue as ContractStatus)} options={STATUS_OPTIONS.map((item) => ({ value: item.code, label: item.label }))} ariaLabel="Trạng thái hợp đồng" />
                </Field>
              </div>
            </div>

            {/* Section 2: Lịch thanh toán */}
            <div className="ctf2-card ctf2-card-pad0">
              <div className="ctf2-schedule-head">
                <div className="ctf2-schedule-head-left">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                  <div className="ctf2-schedule-title">{isExpense ? 'Lịch chi trả' : 'Lịch thanh toán'}</div>
                  <span className="ctf2-schedule-info">
                    {schedule.length} kỳ · Tổng {fmtVND(scheduleTotal)}đ ·
                    {scheduleMatch ? <span className="green"> Khớp giá trị HĐ ✓</span>
                      : value > 0 ? <span className="red"> {scheduleDiff > 0 ? `Thiếu ${fmtVND(scheduleDiff)}đ` : `Thừa ${fmtVND(-scheduleDiff)}đ`}</span>
                      : <span className="gray"> Chưa có giá trị HĐ</span>}
                  </span>
                </div>
                <div className="ctf2-schedule-actions">
                  <button type="button" className="ctf2-btn-split active" onClick={splitEqualMonthly}>Chia đều theo tháng</button>
                  <button type="button" className="ctf2-btn-split" onClick={splitEqualQuarterly}>Chia đều theo quý</button>
                  <button type="button" className="ctf2-btn-split" onClick={addRow}>Nhập thủ công</button>
                </div>
              </div>

              {schedule.length === 0 ? (
                <div className="ctf2-schedule-empty">
                  Chưa có kỳ thanh toán. Chọn "Chia đều theo tháng" (6 kỳ) hoặc "theo quý" (4 kỳ), hoặc "Nhập thủ công".
                </div>
              ) : (
                <table className="ctf2-schedule-tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 88 }}>Kỳ</th>
                      <th style={{ width: 140 }}>Ngày đến hạn</th>
                      <th style={{ textAlign: 'right', width: 180 }}>Số tiền</th>
                      <th>Ghi chú</th>
                      <th style={{ width: 50 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((r, i) => (
                      <tr key={r.id}>
                        <td><span className="ctf2-ky-pill">Kỳ {i + 1}</span></td>
                        <td>
                          <input type="date" value={r.dueDate} onChange={(e) => updateRow(r.id, { dueDate: e.target.value })} />
                        </td>
                        <td>
                          <input
                            inputMode="numeric"
                            value={r.amount ? fmtVND(r.amount) : ''}
                            onChange={(e) => updateRow(r.id, { amount: Number(e.target.value.replace(/\D/g, '')) || 0 })}
                            style={{ textAlign: 'right', fontWeight: 600 }}
                          />
                        </td>
                        <td>
                          <input value={r.note} onChange={(e) => updateRow(r.id, { note: e.target.value })} placeholder="—" />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button type="button" className="ctf2-row-del" onClick={() => removeRow(r.id)} title="Xoá kỳ">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="ctf2-schedule-total">
                      <td colSpan={2}>Tổng cộng · {schedule.length} kỳ</td>
                      <td style={{ textAlign: 'right' }}>{fmtVND(scheduleTotal)}đ</td>
                      <td colSpan={2} style={{ textAlign: 'right' }}>
                        {scheduleMatch ? <span className="ctf2-total-ok">✓ Khớp giá trị HĐ</span>
                          : value > 0 ? <span className="ctf2-total-err">{scheduleDiff > 0 ? `Thiếu ${fmtVND(scheduleDiff)}đ` : `Thừa ${fmtVND(-scheduleDiff)}đ`}</span>
                          : <span className="ctf2-total-gray">Chưa có giá trị HĐ</span>}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
              <button type="button" className="ctf2-add-row-link" onClick={addRow}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5v14" /></svg>
                Thêm kỳ thanh toán
              </button>
            </div>

            {/* Section 3: Tài liệu đính kèm */}
            <div className="ctf2-card">
              <div className="ctf2-card-title">Tài liệu đính kèm</div>
              {(files.length > 0 || uploadedFiles.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  {uploadedFiles.map((f) => (
                    <div key={f.id} className="ctf2-file-tile">
                      <div className="ctf2-file-icon">📄</div>
                      <div style={{ flex: 1, minWidth: 0 }}><div className="ctf2-file-name">{f.fileName}</div><div className="ctf2-file-size">Đã tải lên · {(f.sizeBytes / 1024 / 1024).toFixed(1)} MB</div></div>
                      <button type="button" className="ctf2-file-del" onClick={() => setUploadedFiles((prev) => prev.filter((item) => item.id !== f.id))}>×</button>
                    </div>
                  ))}
                  {files.map((f, i) => (
                    <div key={i} className="ctf2-file-tile">
                      <div className="ctf2-file-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="ctf2-file-name">{f.name}</div>
                        <div className="ctf2-file-size">{(f.size / 1024 / 1024).toFixed(1)} MB</div>
                      </div>
                      <button type="button" className="ctf2-file-del" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="ctf2-dropzone">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const list = Array.from(e.target.files ?? []).filter((f) => f.size <= 10 * 1024 * 1024);
                    setFiles((prev) => [...prev, ...list]);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                <span>Kéo thả hoặc click để upload · PDF/DOCX/JPG · &lt;10MB</span>
              </label>
            </div>

            {/* Section 4: Ghi chú nội bộ */}
            <div className="ctf2-card">
              <div className="ctf2-card-title">Ghi chú nội bộ</div>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={isExpense ? 'VD HĐ ưu tiên · NCC chiến lược · điều khoản bảo mật…' : 'VD HĐ ưu tiên chăm sóc · KH quan trọng · điều khoản bảo mật…'}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e4e7ec', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }}
              />
            </div>

            {err && <div className="cvm-error">{err}</div>}
          </div>

          {/* Footer */}
          <div className="ctf2-foot">
            {isEdit ? (
              <button type="button" onClick={handleDelete} disabled={deleting || saving || savingDraft} className="ctf2-btn-delete">
                {deleting ? 'Đang xoá…' : 'Xoá hợp đồng'}
              </button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button type="button" onClick={onClose} disabled={saving || savingDraft || deleting} className="ctf2-btn-cancel">Hủy</button>
              <button type="button" onClick={handleSaveDraft} disabled={saving || savingDraft || deleting || loadingEdit} className="ctf2-btn-draft">
                {savingDraft ? 'Đang lưu…' : 'Lưu nháp'}
              </button>
              <button type="submit" disabled={saving || savingDraft || deleting || loadingEdit || uploadingFiles} className="ctf2-btn-save">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                {saving || uploadingFiles ? 'Đang lưu…' : (isEdit ? 'Lưu thay đổi' : 'Lưu hợp đồng')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="ctf2-field">
      <label>{label}{required && <span className="req"> *</span>}</label>
      {children}
    </div>
  );
}
